import { useCallback, useEffect, useState } from 'react';
import type { Gap, GapStatus } from '@stonelink/shared';
import { api } from '../api';
import { Badge, Button, EmptyState, ErrorNote, PageHeader, Textarea, formatDate } from '../ui';

/**
 * The gap report: questions the assistant could not answer.
 *
 * Answering one here writes a fact and links it to the gap, so the next visitor
 * who asks gets a real answer. This is the loop that makes the product improve.
 */
export function GapsPage() {
  const [gaps, setGaps] = useState<Gap[]>([]);
  const [status, setStatus] = useState<GapStatus>('open');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setGaps(await api.listGaps(status));
      setError(null);
    } catch {
      setError('Could not load the gap report.');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title="Gaps"
        description="Questions your assistant could not answer. Answer one and it will never be stuck on it again."
      />

      <div className="mb-4 flex gap-1 rounded-xl border border-line bg-white/5 p-1">
        {(['open', 'resolved', 'dismissed'] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setStatus(value)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize transition ${
              status === value ? 'bg-brand text-[#15102a]' : 'text-muted'
            }`}
          >
            {value}
          </button>
        ))}
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {loading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : gaps.length === 0 ? (
        <EmptyState
          title={status === 'open' ? 'No open gaps' : `Nothing ${status}`}
          hint={
            status === 'open'
              ? 'When a visitor asks something your assistant cannot answer, it lands here.'
              : undefined
          }
        />
      ) : (
        <ul className="space-y-3">
          {gaps.map((gap) => (
            <GapRow key={gap.id} gap={gap} onChanged={() => void load()} />
          ))}
        </ul>
      )}
    </div>
  );
}

function GapRow({ gap, onChanged }: { gap: Gap; onChanged: () => void }) {
  const [answering, setAnswering] = useState(false);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolve = async () => {
    const content = answer.trim();
    if (content.length < 3) return;
    setBusy(true);
    try {
      await api.resolveGap(gap.id, content);
      onChanged();
    } catch {
      setError('Could not save that answer.');
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.dismissGap(gap.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="glass-flat p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium">{gap.question}</p>
        <Badge
          tone={gap.status === 'open' ? 'warn' : gap.status === 'resolved' ? 'good' : 'neutral'}
        >
          {gap.status}
        </Badge>
      </div>
      <p className="mt-1 text-xs text-muted">Asked {formatDate(gap.createdAt)}</p>

      {gap.status === 'open' && !answering && (
        <div className="mt-3 flex gap-2">
          <Button onClick={() => setAnswering(true)}>Teach the answer</Button>
          <Button variant="ghost" disabled={busy} onClick={() => void dismiss()}>
            Dismiss
          </Button>
        </div>
      )}

      {answering && (
        <div className="mt-3 space-y-2">
          <Textarea
            rows={3}
            autoFocus
            value={answer}
            aria-label="Answer to teach"
            placeholder="Write the answer as a self-contained fact, e.g. “On-site visits are available within 50 miles for an added fee.”"
            onChange={(event) => setAnswer(event.target.value)}
          />
          {error && <ErrorNote>{error}</ErrorNote>}
          <div className="flex gap-2">
            <Button disabled={busy || answer.trim().length < 3} onClick={() => void resolve()}>
              Save to notebook
            </Button>
            <Button variant="ghost" onClick={() => setAnswering(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
